import { useCallback } from 'react';

import { FormikValues } from 'formik';
import { DateTime } from 'luxon';

import {
  ILongContractAttributes,
} from '@pharmaplan/common';

import { nameTypes } from '../../components/SelfService/Workshifts/WorkshiftForm/ViewEditContract/ViewEditContractTime/ViewEditContractTime';
import { toJSDate } from '../../components/Dashboard/MainCalendar/helpers';
import { appendDateTime } from '../../helpers/workshift/functions';
import { MomentTimeFormats } from '../../helpers/Constants';

const toName = (date: string | undefined, name: keyof typeof nameTypes) =>
  `${date}|${name}`;

const fromName = (name: string) =>
  name.split('|');

const useViewEditContract = () => {
  const intervalsInitial = useCallback(
    (shiftContractAttributes: Array<ILongContractAttributes>) =>
      shiftContractAttributes?.reduce(
        (acc: Record<string, string | null | boolean | Date>, curr) => {
          const date = curr.startDate?.split?.('T')?.[0];
          acc[toName(date, nameTypes.bookedStart)] = toJSDate(curr.startDate);
          acc[toName(date, nameTypes.bookedEnd)] = toJSDate(curr.endDate);
          acc[toName(date, nameTypes.start)] = toJSDate(curr.startDate);
          acc[toName(date, nameTypes.end)] = toJSDate(curr.endDate);
          acc[toName(date, nameTypes.dayOff)] = !!curr?.dayOff;
          acc[toName(date, nameTypes.changed)] = !!curr?.changed;
          acc[toName(date, nameTypes.booked)] = !!curr?.booked;

          return acc;
        },
        {},
      ),
    [],
  );

  const generateTimeIntervals = (shiftContractAttributes: Array<ILongContractAttributes>) =>
    (globalStart: Date, globalEnd: Date) => {
      const startTime = DateTime.fromJSDate(globalStart);
      const endTime = DateTime.fromJSDate(globalEnd);

      return shiftContractAttributes?.filter((item) =>
        !item?.booked)?.reduce(
        (acc: Record<string, string | null | boolean | Date>, curr) => {
          const date = curr.startDate?.split?.('T')?.[0];

          acc[toName(date, nameTypes.start)] = toJSDate(appendDateTime(
            curr.startDate,
            startTime,
          ).toISO({ includeOffset: false }) ?? '');
          acc[toName(date, nameTypes.end)] = toJSDate(appendDateTime(
            curr.endDate,
            endTime,
          ).toISO({ includeOffset: false }) ?? '');
          acc[toName(date, nameTypes.dayOff)] = !!curr?.dayOff;
          acc[toName(date, nameTypes.changed)] = !!curr?.changed;

          return acc;
        },
        {},
      );
    };
  const getFormattedObj = (values: FormikValues) =>
    Object.keys(values)?.reduce(
      (
        acc: Record<
          string,
          {
            startDate: string;
            endDate: string;
            dayOff: boolean;
            changed: boolean;
            booked: boolean;
          }
        >,
        curr,
      ) => {
        const [date, name] = fromName(curr);
        switch (name) {
          case nameTypes.booked:
            acc[date] = {
              ...acc[date],
              booked: values?.[curr],
            };
            break;
          case nameTypes.start:
            acc[date] = {
              ...acc[date],
              startDate: DateTime.fromJSDate(values[curr]).toFormat(
                MomentTimeFormats.fullDate,
              ),
            };
            break;
          case nameTypes.end:
            acc[date] = {
              ...acc[date],
              endDate: DateTime.fromJSDate(values[curr]).toFormat(
                MomentTimeFormats.fullDate,
              ),
            };

            break;
          case nameTypes.changed:
            acc[date] = {
              ...acc[date],
              changed: values[curr],
            };
            break;
          case nameTypes.dayOff:
            acc[date] = {
              ...acc[date],
              dayOff: values[curr],
            };
            break;
          default:
            break;
        }

        return acc;
      },
      {},
    );

  return { intervalsInitial, getFormattedObj, generateTimeIntervals };
};

export default useViewEditContract;
